import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Typography } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { previewFormCoborrowersOptions, previewFormNonborrowersOptions } from 'constants/options';
import useForm from 'hooks/useForm';

import { openFormPreviewAction } from 'store/forms/actions';
import { closeDialogAction } from 'store/global/actions';

import { formsTestIds } from '../forms.testIds';

const PrePreviewFormModal = ({ formId, historyEntityId, formType, selectedPage }) => {
  const dispatch = useDispatch();

  const { form, isFormChanged, setInForm, validateField } = useForm({
    coborrowersNumber: previewFormCoborrowersOptions[0],
    nonborrowersNumber: previewFormNonborrowersOptions[0],
  });

  const coborrowersNumberInForm = form.coborrowersNumber.value;
  const nonborrowersNumberInForm = form.nonborrowersNumber.value;

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      dispatch(
        openFormPreviewAction({
          formId,
          historyEntityId,
          formType,
          selectedPage,
          coborrowersNumber: coborrowersNumberInForm,
          nonborrowersNumber: nonborrowersNumberInForm,
        }),
      );

      dispatch(closeDialogAction());
    },
    [
      dispatch,
      formId,
      historyEntityId,
      formType,
      selectedPage,
      coborrowersNumberInForm,
      nonborrowersNumberInForm,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <form
      noValidate
      className="flex flex-col justify-center p-24"
      data-ischanged={isFormChanged}
      onSubmit={onSubmit}
    >
      <AutocompleteCDS
        disableClearable
        className="my-8"
        dataTestId={formsTestIds.PRE_PREVIEW_FORM_COBORROWERS_SELECT}
        label="Number of co-borrowers"
        name="coborrowersNumber"
        options={previewFormCoborrowersOptions}
        value={form.coborrowersNumber || ''}
        onChange={onAutocompleteChange}
      />

      <AutocompleteCDS
        disableClearable
        className="my-8"
        dataTestId={formsTestIds.PRE_PREVIEW_FORM_NONBORROWERS_SELECT}
        label="Number of NBEs"
        name="nonborrowersNumber"
        options={previewFormNonborrowersOptions}
        value={form.nonborrowersNumber || ''}
        onChange={onAutocompleteChange}
      />

      <Typography className="italic" color="textSecondary" variant="regular12">
        The application includes Primary Borrower
        {coborrowersNumberInForm
          ? `${nonborrowersNumberInForm ? ',' : ' and'} ${coborrowersNumberInForm} Co-borrower${
              coborrowersNumberInForm > 1 ? 's' : ''
            }`
          : ``}
        {nonborrowersNumberInForm
          ? ` and ${nonborrowersNumberInForm} NBE${nonborrowersNumberInForm > 1 ? 's' : ''}`
          : ``}
        .
      </Typography>

      <Button
        className="whitespace-nowrap self-end"
        data-testid={formsTestIds.PRE_PREVIEW_FORM_SUBMIT_BUTTON}
        type="submit"
        variant="contained"
      >
        Open
      </Button>
    </form>
  );
};

PrePreviewFormModal.propTypes = {
  formId: PropTypes.string.isRequired,
  historyEntityId: PropTypes.string.isRequired,
  formType: PropTypes.string.isRequired,
  selectedPage: PropTypes.string.isRequired,
};

export default memo(PrePreviewFormModal);
